import { api, endpoints } from "api/api";
import { BaseApiPaging } from "api/types";
import { AxiosPromise } from "axios";
import { CourseBaseInfoDto } from "../courses/coursesApi";

const fetch = (
  skip: number,
  take: number,
  searchPhrase: string = "",
  filter: string = "",
  orderBy: string = "",
): AxiosPromise<BaseApiPaging<DiscountCodeApi>> => {
  return api.get<BaseApiPaging<DiscountCodeApi>>(endpoints.DISCOUNTS.GET(skip, take, searchPhrase, filter, orderBy));
};

const fetchRegistrations = (paymentDiscountId: number): AxiosPromise<PaymentDiscountRegistrationApi[]> => {
  return api.get<PaymentDiscountRegistrationApi[]>(endpoints.DISCOUNTS.GET_REGISTRATIONS(paymentDiscountId));
};
const fetchById = (discountId: number): AxiosPromise<DiscountCodeApi> => {
  return api.get<DiscountCodeApi>(endpoints.DISCOUNTS.GET_BY_ID(discountId));
};

const deleteDiscount = (discountId: number): Promise<void> => {
  return api.delete(endpoints.DISCOUNTS.DELETE(discountId));
};

const create = (discount: DiscountModel): Promise<void> => {
  return api.post(endpoints.DISCOUNTS.CREATE(), discount);
};

const update = (discountId: number, discount: DiscountModel): Promise<void> => {
  return api.put(endpoints.DISCOUNTS.UPDATE(discountId), discount);
};

// Bundle discount API functions
const createBundle = (discount: DiscountModel): Promise<number> => {
  return api.post(endpoints.DISCOUNTS.BUNDLE.CREATE(), discount);
};

const updateBundle = (discountId: number, discount: DiscountModel): Promise<number> => {
  return api.put(endpoints.DISCOUNTS.BUNDLE.UPDATE(discountId), discount);
};

const setDisabled = (discountId: number, isDisabled: boolean): Promise<void> => {
  return api.patch(endpoints.DISCOUNTS.SET_DISABLED(discountId), { isDisabled });
};

export const discountsApi = {
  fetch,
  fetchById,
  delete: deleteDiscount,
  create,
  update,
  fetchRegistrations,
  // Bundle discount operations
  createBundle,
  updateBundle,
  // Per-discount kill switch
  setDisabled,
};

export type BundleRequirementMode = "courseList" | "courseGroupList" | "categoryMinCount";

// Server returns enum as integer; mirror that for clarity.
// 0 = CourseList, 1 = CourseGroupList, 2 = CategoryMinCount
export const BundleRequirementModeValue = {
  CourseList: 0,
  CourseGroupList: 1,
  CategoryMinCount: 2,
} as const;

export interface DiscountCodeApi {
  id: number;
  code: string;
  description: string;
  mode: string; // "standard" | "bundle"
  // Standard mode relationships
  course?: CourseBaseInfoDto;
  courseGroup?: { id: number; name: string };
  categories?: { id: number; name: string }[];
  user?: { id: number; email: string };
  // Bundle mode relationships
  requiredCourses?: CourseBaseInfoDto[];
  benefitCourses?: CourseBaseInfoDto[];
  requiredCourseGroups?: { id: number; name: string }[];
  benefitCourseGroups?: { id: number; name: string }[];
  bundleRequirementMode?: BundleRequirementMode | number | null;
  bundleCategoryId?: number | null;
  bundleCategoryName?: string | null;
  bundleMinCount?: number | null;
  isDisabled: boolean;
  autoApply: boolean;
  from: Date;
  to: Date;
  numberOfUses: number;
  type: number;
  value: number;
  timesUsed: number;
  createdDate: Date;
}

export interface DiscountModel {
  code?: string;
  description: string;
  from?: Date | null;
  to?: Date | null;
  numberOfUses?: number | null;
  type: number;
  value: number;
  // Mode selector
  mode?: string; // "standard" | "bundle"
  // Standard mode fields
  courseId?: number;
  courseGroupId?: number;
  categoryIds?: number[];
  userId?: number;
  // Bundle mode fields
  requirementMode?: number; // 0/1/2 per BundleRequirementModeValue
  requiredCourseIds?: number[];
  benefitCourseIds?: number[];
  requiredCourseGroupIds?: number[];
  benefitCourseGroupIds?: number[];
  bundleCategoryId?: number | null;
  bundleMinCount?: number | null;
  autoApply?: boolean;
  applyAutomatically?: boolean;
  applyRetroactively?: boolean;
}
export interface PaymentDiscountRegistrationApi {
  paymentDiscountRegistrationId: number;
  originalPrice: number;
  discountedPrice: number;
  userEmail: string;
  userFirstName: string;
  userSurname: string;
  courseId: number;
  courseName: string;
  createdDate: Date;
}
